<script setup lang="ts">
import { computed, ref } from 'vue'
import { NButton, NEmpty, NSelect } from 'naive-ui'
import type { AgentChannelSummary } from '../../stores/agent_channels'
import type { AgentSessionSummary } from '../../stores/agent'
import { shortThread } from '../../composables/shortThread'

const props = defineProps<{ channel: AgentChannelSummary; sessions: AgentSessionSummary[] }>()
const emit = defineEmits<{ add: [threadId: string]; remove: [threadId: string] }>()

const picked = ref<string | null>(null)

const sessionById = computed(() => new Map(props.sessions.map((s) => [s.thread_id, s])))

const members = computed(() =>
  props.channel.member_thread_ids.map((id) => {
    const session = sessionById.value.get(id)
    return { id, name: session?.name || shortThread(id), repo: session?.repo ?? null }
  }),
)

const options = computed(() =>
  props.sessions
    .filter((s) => !props.channel.member_thread_ids.includes(s.thread_id))
    .map((s) => ({ value: s.thread_id, label: `${s.name || shortThread(s.thread_id)} · ${s.repo}` })),
)

function add(): void {
  if (!picked.value) return
  emit('add', picked.value.trim())
  picked.value = null
}
</script>

<template>
  <section class="flex flex-col gap-3" aria-label="Members" data-testid="members-panel">
    <h2 class="text-sm font-semibold text-white/80 m-0">Members ({{ channel.member_count }})</h2>

    <NEmpty
      v-if="members.length === 0"
      size="small"
      description="No threads in this channel. Add one so it can read and post."
      data-testid="members-empty"
    />
    <ul v-else class="list-none m-0 p-0 flex flex-col gap-1" data-testid="member-list">
      <li v-for="m in members" :key="m.id" class="flex items-center justify-between gap-2 text-sm text-white/80">
        <span class="flex flex-col min-w-0">
          <span class="truncate">{{ m.name }}</span>
          <span v-if="m.repo" class="text-xs text-white/60 font-mono truncate">{{ m.repo }}</span>
        </span>
        <NButton
          size="tiny"
          tertiary
          :aria-label="`Remove ${m.name}`"
          :data-testid="`remove-member-${m.id}`"
          @click="emit('remove', m.id)"
        >
          Remove
        </NButton>
      </li>
    </ul>

    <div class="flex gap-2">
      <NSelect
        v-model:value="picked"
        class="flex-1 min-w-0"
        filterable
        tag
        clearable
        size="small"
        :options="options"
        placeholder="Pick a session or paste a thread id"
        data-testid="member-input"
      />
      <NButton size="small" :disabled="!picked" data-testid="member-add" @click="add">Add</NButton>
    </div>
  </section>
</template>
